import { motion } from "motion/react";
import { Sparkles, AlertTriangle } from "lucide-react";
import { cn } from "@/modern/utils";
import { useFileDetailModal } from "../../../context/FileDetailModalContext";

export const AiObservationsCard = ({
  items,
  compact,
  className,
}: {
  items: string[];
  compact?: boolean;
  className?: string;
}) => {
  if (items.length === 0) return null;
  return (
    <div
      className={cn(
        "rounded-xl bg-gradient-to-br from-violet-600/90 to-purple-700/90 border border-violet-400/20 shadow-2xl shadow-violet-900/30",
        compact ? "p-2.5" : "p-3.5",
        className,
      )}
    >
      <div className={cn("flex items-center gap-2", compact ? "mb-1.5" : "mb-2.5")}>
        <div className={cn("rounded-md bg-white/15 flex items-center justify-center", compact ? "w-4 h-4" : "w-5 h-5")}>
          <Sparkles className={cn("text-white", compact ? "w-2.5 h-2.5" : "w-3 h-3")} />
        </div>
        <span className={cn("font-bold uppercase tracking-wider text-white/90", compact ? "text-[10px]" : "text-xs")}>
          AI Observations
        </span>
      </div>
      <ol className="space-y-1.5 list-none m-0 p-0">
        {items.map((item, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 + i * 0.08 }}
            className={cn("text-white/85 leading-relaxed flex gap-1.5", compact ? "text-xs" : "text-sm")}
          >
            <span className="text-white/60 font-medium shrink-0">{i + 1}.</span>
            <span>{item}</span>
          </motion.li>
        ))}
      </ol>
    </div>
  );
};

export const AiWarningsCard = ({
  items,
  compact,
  delay,
  className,
}: {
  items: string[];
  compact?: boolean;
  delay?: number;
  className?: string;
}) => {
  if (items.length === 0) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delay ?? 0 }}
    >
      <div
        className={cn(
          "rounded-xl bg-gradient-to-br from-amber-600/90 to-orange-700/90 border border-amber-400/20 shadow-2xl shadow-amber-900/30",
          compact ? "p-2.5" : "p-3.5",
          className,
        )}
      >
        <div className={cn("flex items-center gap-2", compact ? "mb-1.5" : "mb-2.5")}>
          <div className={cn("rounded-md bg-white/15 flex items-center justify-center", compact ? "w-4 h-4" : "w-5 h-5")}>
            <AlertTriangle className={cn("text-white", compact ? "w-2.5 h-2.5" : "w-3 h-3")} />
          </div>
          <span className={cn("font-bold uppercase tracking-wider text-white/90", compact ? "text-[10px]" : "text-xs")}>
            Visibility Warnings
          </span>
        </div>
        <ol className="space-y-1.5 list-none m-0 p-0">
          {items.map((item, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className={cn("text-white/85 leading-relaxed flex gap-1.5", compact ? "text-xs" : "text-sm")}
            >
              <span className="text-white/60 font-medium shrink-0">{i + 1}.</span>
              <span>{item}</span>
            </motion.li>
          ))}
        </ol>
      </div>
    </motion.div>
  );
};

export const AiInsightOverlay = () => {
  const { promoteAiSuggestions, promoteVisibilityWarnings, isPromotePanelOpen } = useFileDetailModal();

  const hasObservations = promoteAiSuggestions.length > 0;
  const hasWarnings = promoteVisibilityWarnings.length > 0;

  if (!isPromotePanelOpen || (!hasObservations && !hasWarnings)) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 16, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="absolute top-6 right-6 z-20 max-w-[280px] space-y-2.5"
    >
      <AiObservationsCard items={promoteAiSuggestions} className="backdrop-blur-md" />
      <AiWarningsCard
        items={promoteVisibilityWarnings}
        delay={hasObservations ? 0.15 : 0}
        className="backdrop-blur-md"
      />
    </motion.div>
  );
};
